import React, { useMemo } from "react";
import { Select } from "@pd/pd-ui-kit";
import { useTranslation } from "react-i18next";

import { useGetEmployeesForSelect } from "~/entity/Employee";
import { handlePopupScroll } from "~/pages/Directory/shared/handlePopupScroll";

export interface ISelectValue {
  value: string;
  label: string;
}

interface EmployeeSelectProps {
  enabled: boolean;
  dealerId?: number;
  positionInstanceIds: number[];
  excludeValues?: string[];
  value: ISelectValue | null;
  onChange: (value: ISelectValue | null) => void;
  hasError?: boolean;
}

const EmployeeSelect = ({
  enabled,
  dealerId,
  positionInstanceIds,
  excludeValues,
  value,
  onChange,
  hasError,
}: EmployeeSelectProps) => {
  const { t } = useTranslation();
  const employeesSelect = useGetEmployeesForSelect(
    enabled && dealerId != null,
    { dealerId, positionInstanceIds },
  );

  const options = useMemo(() => {
    const excluded = new Set(excludeValues ?? []);
    const merged =
      value &&
      !employeesSelect.options.some((option) => option.value === value.value)
        ? [...employeesSelect.options, { ...value, dealerName: "" }]
        : employeesSelect.options;

    return merged.filter(
      (option) => option.value === value?.value || !excluded.has(option.value),
    );
  }, [employeesSelect.options, excludeValues, value]);

  const onPopupScroll = (event: React.UIEvent<HTMLDivElement>) =>
    handlePopupScroll(event, employeesSelect.onScrollBottom);

  return (
    <Select
      labelInValue
      allowClear
      showSearch
      filterOption={false}
      disabled={dealerId == null}
      value={value}
      onChange={(option) => onChange(option ?? null)}
      options={options}
      loading={employeesSelect.loading}
      onSearch={employeesSelect.onSearch}
      onOpenChange={employeesSelect.onOpenChange}
      onPopupScroll={onPopupScroll}
      status={hasError ? "error" : undefined}
      placeholder={t("directory.baseTariffs.modal.selectPlaceholder")}
    />
  );
};

export default EmployeeSelect;
